import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import {
  gatewayFormInputCreate,
  gatewayFormInputUpdate,
  type GatewayDetail,
  type GatewayFormInput,
} from '../api/schemas.js';
import { useCreateGateway, useUpdateGateway } from '../api/mutations.js';
import { useGateway } from '../api/queries.js';
import { NotificationGatewayFields } from '../forms/notification-gateway-fields.js';

export const CREATE_DEFAULTS: GatewayFormInput = {
  channel: '',
  provider: '',
  name: '',
  fromAddress: '',
  fromName: '',
  isDefault: false,
  isActive: true,
  priority: '0',
  smtpHost: '',
  smtpPort: '',
  smtpUsername: '',
  smtpPassword: '',
  smtpSecure: false,
  sendgridApiKey: '',
  sesRegion: '',
  sesAccessKeyId: '',
  sesSecretAccessKey: '',
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioFromNumber: '',
  vonageApiKey: '',
  vonageApiSecret: '',
  snsSenderId: '',
  snsRegion: '',
  snsAccessKeyId: '',
  snsSecretAccessKey: '',
  snsSmsType: '',
  snsWebhookSecret: '',
  infobipSenderId: '',
  infobipApiBaseUrl: '',
  infobipApiKey: '',
  infobipWebhookSecret: '',
};

function defaultsFrom(gateway: GatewayDetail): GatewayFormInput {
  return {
    ...CREATE_DEFAULTS,
    channel: gateway.channel,
    provider: gateway.provider,
    name: gateway.name,
    fromAddress: gateway.fromAddress ?? '',
    fromName: gateway.fromName ?? '',
    isDefault: gateway.isDefault,
    isActive: gateway.isActive,
    priority: String(gateway.priority),
  };
}

export function NotificationGatewayFormPage(): React.JSX.Element {
  const gatewayId = useParams().gatewayId ?? '';
  const isEdit = gatewayId !== '';

  return (
    <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE} fallback={<NotPermitted />}>
      {isEdit ? <EditGateway gatewayId={gatewayId} /> : <CreateGateway />}
    </PermissionGuard>
  );
}

function CreateGateway(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreateGateway();

  const form = useAppForm<GatewayFormInput>({
    schema: gatewayFormInputCreate,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Gateway created',
    onSubmit: async (values) => {
      await create.mutateAsync(values);
      void navigate('/notifications');
    },
  });

  return (
    <FormPage
      title="New notification gateway"
      description="A gateway is a provider connection the platform sends email and SMS through."
      backTo="/notifications"
      backLabel="Notification gateways"
      submitLabel="Create gateway"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <NotificationGatewayFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="create"
      />
    </FormPage>
  );
}

function EditGateway({ gatewayId }: { readonly gatewayId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateGateway();
  const query = useGateway(gatewayId);

  if (query.isPending) return <LoadingState label="Loading gateway" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditGatewayForm
      gateway={query.data}
      onSaved={() => {
        void navigate('/notifications');
      }}
      save={async (values) => {
        await update.mutateAsync({ gatewayId, values });
      }}
    />
  );
}

function EditGatewayForm({
  gateway,
  onSaved,
  save,
}: {
  readonly gateway: GatewayDetail;
  readonly onSaved: () => void;
  readonly save: (values: GatewayFormInput) => Promise<void>;
}): React.JSX.Element {
  const form = useAppForm<GatewayFormInput>({
    schema: gatewayFormInputUpdate,
    defaultValues: defaultsFrom(gateway),
    successMessage: 'Gateway updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title="Edit notification gateway"
      description="Change the gateway's settings. Credentials are not shown for security — enter a complete set only when you want to replace them."
      backTo="/notifications"
      backLabel="Notification gateways"
      submitLabel="Save gateway"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <NotificationGatewayFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="edit"
      />
    </FormPage>
  );
}
